import { useEffect, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";

interface FullscreenButtonProps {
  /** Ref to the element that should go fullscreen (must contain video + watermark) */
  targetRef: React.RefObject<HTMLElement>;
}

/**
 * Custom fullscreen button.
 *
 * The native HTML <video> fullscreen only fullscreens the video element itself,
 * which hides our forensic watermark overlay. This button calls
 * requestFullscreen() on the *container* so video AND watermark go fullscreen
 * together.
 *
 * Pair with `controlsList="nofullscreen"` on the <video> to remove the native
 * fullscreen control and prevent users from bypassing the watermark.
 */
export const FullscreenButton = ({ targetRef }: FullscreenButtonProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => {
      setIsFullscreen(document.fullscreenElement === targetRef.current);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, [targetRef]);

  const toggle = async () => {
    const el = targetRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch (err) {
      console.warn("Fullscreen toggle failed:", err);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
      className="absolute top-2 right-2 z-40 inline-flex items-center justify-center w-9 h-9 rounded-md bg-black/55 hover:bg-black/75 text-white backdrop-blur-sm transition-colors"
      style={{ pointerEvents: "auto" }}
    >
      {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
    </button>
  );
};

export default FullscreenButton;
